import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  IconButton,
  Typography,
  Paper,
  Snackbar,
  Alert,
  Tooltip,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItemButton,
  ListItemText,
  Menu,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import { getToken } from "../services/auth";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const REPORT_OPTIONS = [
  { label: "Demand Report",          key: "demand_report_column_visibility" },
  { label: "PDP Report",             key: "pdp_report_column_visibility" },
  { label: "VCDP Report",            key: "vcdp_report_column_visibility" },
  { label: "NBL Report",             key: "nbl_report_column_visibility" },
  { label: "Lateral Hiring Report",  key: "lateral_hiring_report_column_visibility" },
  { label: "Rotation List Report",   key: "rotation_list_report_column_visibility" },
];

// ─── Editable Field (key) — looks like plain text, double-click to edit ──────
function EditableField({ value, onChange, sx }) {
  const [editing, setEditing] = useState(false);
  const [localVal, setLocalVal] = useState(value);
  const inputRef = useRef(null);

  useEffect(() => { setLocalVal(value); }, [value]);
  useEffect(() => { if (editing && inputRef.current) inputRef.current.focus(); }, [editing]);

  if (!editing) {
    return (
      <Typography
        variant="body2"
        onDoubleClick={(e) => { e.stopPropagation(); setEditing(true); }}
        sx={{
          minWidth: 220,
          fontFamily: "monospace",
          fontSize: "0.75rem",
          cursor: "default",
          px: 1,
          py: 0.5,
          borderRadius: 0.5,
          "&:hover": { bgcolor: "action.hover" },
          userSelect: "none",
          ...sx,
        }}
        title="Double-click to edit field key"
      >
        {value || <em style={{ color: "#bbb" }}>empty</em>}
      </Typography>
    );
  }

  return (
    <TextField
      inputRef={inputRef}
      value={localVal}
      onChange={(e) => setLocalVal(e.target.value)}
      onBlur={() => { onChange(localVal); setEditing(false); }}
      onKeyDown={(e) => {
        if (e.key === "Enter") { onChange(localVal); setEditing(false); }
        if (e.key === "Escape") { setLocalVal(value); setEditing(false); }
      }}
      size="small"
      variant="outlined"
      sx={{ minWidth: 220, fontFamily: "monospace", fontSize: "0.75rem" }}
      inputProps={{ style: { fontFamily: "monospace", fontSize: "0.75rem" } }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    />
  );
}

// ─── Draggable row component for Table Template ──────────────────────────────
function DraggableRow({ index, item, allItems, onHeaderChange, onFieldChange, onToggleHidden, onDelete, onMoveAfter, onDragStart, onDragEnter, onDragEnd, isDragOver }) {
  const isHidden = Boolean(item.hidden);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);

  const handleMenuOpen = (e) => { e.stopPropagation(); setMenuAnchor(e.currentTarget); };
  const handleMenuClose = () => setMenuAnchor(null);

  return (
    <>
      <Box
        draggable
        onDragStart={() => onDragStart(index)}
        onDragEnter={() => onDragEnter(index)}
        onDragEnd={onDragEnd}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 1,
          py: 0.75,
          mb: 0.5,
          border: "1px solid",
          borderColor: isDragOver ? "primary.main" : "divider",
          borderRadius: 1,
          bgcolor: isDragOver ? "primary.50" : isHidden ? "grey.50" : "background.paper",
          opacity: isHidden ? 0.55 : 1,
          cursor: "grab",
          transition: "border-color 0.15s, background-color 0.15s, opacity 0.15s",
          "&:active": { cursor: "grabbing" },
        }}
      >
        <DragIndicatorIcon sx={{ color: "text.disabled", flexShrink: 0 }} />
        <Typography variant="caption" sx={{ minWidth: 28, color: "text.secondary" }}>
          {index + 1}
        </Typography>
        <EditableField
          value={item.field}
          onChange={(val) => onFieldChange(index, val)}
          sx={{
            color: isHidden ? "text.disabled" : "text.secondary",
            textDecoration: isHidden ? "line-through" : "none",
          }}
        />
        <TextField
          value={item.headerName}
          onChange={(e) => onHeaderChange(index, e.target.value)}
          size="small"
          label="Header Name"
          variant="outlined"
          disabled={isHidden}
          sx={{ flex: 1 }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        />
        <Tooltip title={isHidden ? "Show column in report" : "Hide column from report"}>
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onToggleHidden(index); }}
            onMouseDown={(e) => e.stopPropagation()}
            color={isHidden ? "default" : "primary"}
          >
            {isHidden ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Actions">
          <IconButton size="small" onClick={handleMenuOpen} onMouseDown={(e) => e.stopPropagation()}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
          <MenuItem onClick={() => { handleMenuClose(); setMoveDialogOpen(true); }}>
            <SwapVertIcon fontSize="small" sx={{ mr: 1 }} /> Move After…
          </MenuItem>
          <MenuItem onClick={() => { handleMenuClose(); onDelete(index); }} sx={{ color: "error.main" }}>
            <DeleteOutlineIcon fontSize="small" sx={{ mr: 1 }} /> Delete
          </MenuItem>
        </Menu>
      </Box>

      {/* Move After dialog */}
      <Dialog open={moveDialogOpen} onClose={() => setMoveDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Move "{item.headerName || item.field}" after…</DialogTitle>
        <DialogContent dividers sx={{ maxHeight: 400, p: 0 }}>
          <List dense>
            <ListItemButton onClick={() => { onMoveAfter(index, -1); setMoveDialogOpen(false); }}>
              <ListItemText primary="⬆ Move to top (first position)" primaryTypographyProps={{ fontWeight: 600 }} />
            </ListItemButton>
            {allItems.map((col, colIdx) => {
              if (colIdx === index) return null;
              return (
                <ListItemButton key={colIdx} onClick={() => { onMoveAfter(index, colIdx); setMoveDialogOpen(false); }}>
                  <ListItemText
                    primary={`${colIdx + 1}. ${col.headerName || col.field}`}
                    secondary={col.field}
                    primaryTypographyProps={{ fontSize: "0.85rem" }}
                    secondaryTypographyProps={{ fontFamily: "monospace", fontSize: "0.7rem" }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMoveDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ─── Filter Template row (draggable) ─────────────────────────────────────────
function FilterRow({ index, item, onHeaderChange, onFieldChange, onCustomSortChange, onDragStart, onDragEnter, onDragEnd, isDragOver, onDelete }) {
  return (
    <Box
      draggable
      onDragStart={() => onDragStart(index)}
      onDragEnter={() => onDragEnter(index)}
      onDragEnd={onDragEnd}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 1,
        py: 0.75,
        mb: 0.5,
        border: "1px solid",
        borderColor: isDragOver ? "primary.main" : "divider",
        borderRadius: 1,
        bgcolor: isDragOver ? "primary.50" : "background.paper",
        cursor: "grab",
        transition: "border-color 0.15s, background-color 0.15s",
        "&:active": { cursor: "grabbing" },
      }}
    >
      <DragIndicatorIcon sx={{ color: "text.disabled", flexShrink: 0 }} />
      <Typography variant="caption" sx={{ minWidth: 24, color: "text.secondary" }}>
        {index + 1}
      </Typography>
      <TextField
        value={item.field}
        onChange={(e) => onFieldChange(index, e.target.value)}
        size="small"
        label="Field"
        variant="outlined"
        sx={{ width: 180, fontFamily: "monospace", flexShrink: 0 }}
        inputProps={{ style: { fontFamily: "monospace", fontSize: "0.8rem" } }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      />
      <TextField
        value={item.headerName}
        onChange={(e) => onHeaderChange(index, e.target.value)}
        size="small"
        label="Header Name"
        variant="outlined"
        sx={{ width: 150, flexShrink: 0 }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      />
      <TextField
        value={item.customSortOrder || ""}
        onChange={(e) => onCustomSortChange(index, e.target.value)}
        size="small"
        label="Custom Sort Order"
        variant="outlined"
        placeholder="e.g. India,North America"
        multiline
        minRows={1}
        maxRows={3}
        sx={{ flex: 1, minWidth: 200 }}
        inputProps={{ style: { fontSize: "0.8rem" } }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        title="Comma-separated values to appear first in filter dropdown"
      />
      <Tooltip title="Delete">
        <IconButton
          size="small"
          color="error"
          onClick={(e) => { e.stopPropagation(); onDelete(index); }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function ReportConfigEditor() {
  const [selectedKey, setSelectedKey] = useState(REPORT_OPTIONS[0].key);
  const [tab, setTab] = useState(0);
  const [tableTemplate, setTableTemplate] = useState([]);
  const [filterTemplate, setFilterTemplate] = useState([]);
  const [reportLabel, setReportLabel] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" });

  // Drag state refs — table template
  const dragIndex = useRef(null);
  const dragOverIndex = useRef(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  // Drag state refs — filter template
  const fltDragIndex = useRef(null);
  const fltDragOverIndex = useRef(null);
  const [fltDragOverIdx, setFltDragOverIdx] = useState(null);

  // ── Fetch config whenever selectedKey changes ────────────────────────────
  const fetchConfig = useCallback(async (key) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/report-config/${key}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setReportLabel(data.report_label || key);
      setTableTemplate(data.table_template || []);
      setFilterTemplate(data.filter_template || []);
    } catch (err) {
      console.error("Failed to load config:", err);
      setSnack({ open: true, msg: "Failed to load config from server.", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig(selectedKey);
  }, [selectedKey, fetchConfig]);

  // ── Save ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/report-config/${selectedKey}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ reportLabel, tableTemplate, filterTemplate }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      setSnack({ open: true, msg: "Config saved successfully!", severity: "success" });
    } catch (err) {
      setSnack({ open: true, msg: `Save failed: ${err.message}`, severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  // ── Table Template handlers ───────────────────────────────────────────────
  const handleTblHeaderChange = (idx, value) => {
    setTableTemplate((prev) => prev.map((r, i) => (i === idx ? { ...r, headerName: value } : r)));
  };

  const handleTblFieldChange = (idx, value) => {
    setTableTemplate((prev) => prev.map((r, i) => (i === idx ? { ...r, field: value } : r)));
  };

  const handleTblToggleHidden = (idx) => {
    setTableTemplate((prev) => prev.map((r, i) => (i === idx ? { ...r, hidden: !r.hidden } : r)));
  };

  const handleTblDelete = (idx) => {
    setTableTemplate((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleTblAdd = () => {
    setTableTemplate((prev) => [...prev, { field: "", headerName: "", hidden: false }]);
  };

  const handleTblMoveAfter = (fromIdx, afterIdx) => {
    setTableTemplate((prev) => {
      const updated = [...prev];
      const [moved] = updated.splice(fromIdx, 1);
      if (afterIdx === -1) {
        updated.splice(0, 0, moved);
      } else {
        const adjustedIdx = fromIdx < afterIdx ? afterIdx : afterIdx + 1;
        updated.splice(adjustedIdx, 0, moved);
      }
      return updated;
    });
  };

  const handleDragStart = (idx) => {
    dragIndex.current = idx;
  };

  const handleDragEnter = (idx) => {
    dragOverIndex.current = idx;
    setDragOverIdx(idx);
  };

  const handleDragEnd = () => {
    const from = dragIndex.current;
    const to = dragOverIndex.current;
    if (from !== null && to !== null && from !== to) {
      setTableTemplate((prev) => {
        const updated = [...prev];
        const [moved] = updated.splice(from, 1);
        updated.splice(to, 0, moved);
        return updated;
      });
    }
    dragIndex.current = null;
    dragOverIndex.current = null;
    setDragOverIdx(null);
  };

  // ── Filter Template handlers ──────────────────────────────────────────────
  const handleFltHeaderChange = (idx, value) => {
    setFilterTemplate((prev) => prev.map((r, i) => (i === idx ? { ...r, headerName: value } : r)));
  };

  const handleFltFieldChange = (idx, value) => {
    setFilterTemplate((prev) => prev.map((r, i) => (i === idx ? { ...r, field: value } : r)));
  };

  const handleFltCustomSortChange = (idx, value) => {
    setFilterTemplate((prev) => prev.map((r, i) => (i === idx ? { ...r, customSortOrder: value } : r)));
  };

  const handleFltDragStart = (idx) => {
    fltDragIndex.current = idx;
  };

  const handleFltDragEnter = (idx) => {
    fltDragOverIndex.current = idx;
    setFltDragOverIdx(idx);
  };

  const handleFltDragEnd = () => {
    const from = fltDragIndex.current;
    const to = fltDragOverIndex.current;
    if (from !== null && to !== null && from !== to) {
      setFilterTemplate((prev) => {
        const updated = [...prev];
        const [moved] = updated.splice(from, 1);
        updated.splice(to, 0, moved);
        return updated;
      });
    }
    fltDragIndex.current = null;
    fltDragOverIndex.current = null;
    setFltDragOverIdx(null);
  };

  const handleFltDelete = (idx) => {
    setFilterTemplate((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleFltAdd = () => {
    setFilterTemplate((prev) => [...prev, { field: "", headerName: "" }]);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3, width: '100%', maxWidth: '100%', mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>
          Report Config Editor
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving || loading}
          sx={{ minWidth: 120 }}
        >
          {saving ? <CircularProgress size={20} color="inherit" /> : "Save"}
        </Button>
      </Box>

      {/* Report selector */}
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 300 }}>
          <InputLabel>Select Report</InputLabel>
          <Select
            value={selectedKey}
            label="Select Report"
            onChange={(e) => setSelectedKey(e.target.value)}
          >
            {REPORT_OPTIONS.map((opt) => (
              <MenuItem key={opt.key} value={opt.key}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {reportLabel && (
          <Typography variant="caption" sx={{ ml: 2, color: "text.secondary" }}>
            DB label: <strong>{reportLabel}</strong> &nbsp;|&nbsp; key: <code>{selectedKey}</code>
          </Typography>
        )}
      </Paper>

      {/* Tabs */}
      <Paper elevation={1} sx={{ p: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label={`Table Template (${tableTemplate.filter(c => !c.hidden).length} visible / ${tableTemplate.length} total)`} />
          <Tab label={`Filter Template (${filterTemplate.length} filters)`} />
        </Tabs>
        <Divider sx={{ mb: 2 }} />

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* ── TABLE TEMPLATE TAB ── */}
            {tab === 0 && (
              <Box>
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1.5 }}>
                  Drag rows to reorder. Double-click the field name to edit it. Use the eye icon to show/hide. Use ⋮ menu for Delete or Move After.
                </Typography>
                <Box
                  onDragOver={(e) => e.preventDefault()}
                  sx={{ maxHeight: "calc(100vh - 380px)", overflowY: "auto", pr: 0.5 }}
                >
                  {tableTemplate.map((item, idx) => (
                    <DraggableRow
                      key={idx}
                      index={idx}
                      item={item}
                      allItems={tableTemplate}
                      onHeaderChange={handleTblHeaderChange}
                      onFieldChange={handleTblFieldChange}
                      onToggleHidden={handleTblToggleHidden}
                      onDelete={handleTblDelete}
                      onMoveAfter={handleTblMoveAfter}
                      onDragStart={handleDragStart}
                      onDragEnter={handleDragEnter}
                      onDragEnd={handleDragEnd}
                      isDragOver={dragOverIdx === idx}
                    />
                  ))}
                </Box>
                <Button
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={handleTblAdd}
                  variant="outlined"
                  size="small"
                  sx={{ mt: 1.5 }}
                >
                  Add Column
                </Button>
              </Box>
            )}

            {/* ── FILTER TEMPLATE TAB ── */}
            {tab === 1 && (
              <Box>
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1.5 }}>
                  Drag rows to reorder filters. Edit Field and Header Name. The <em>Field</em> must match the database column name.
                </Typography>
                <Box
                  onDragOver={(e) => e.preventDefault()}
                  sx={{ maxHeight: "calc(100vh - 380px)", overflowY: "auto", pr: 0.5 }}
                >
                  {filterTemplate.map((item, idx) => (
                    <FilterRow
                      key={idx}
                      index={idx}
                      item={item}
                      onHeaderChange={handleFltHeaderChange}
                      onFieldChange={handleFltFieldChange}
                      onCustomSortChange={handleFltCustomSortChange}
                      onDragStart={handleFltDragStart}
                      onDragEnter={handleFltDragEnter}
                      onDragEnd={handleFltDragEnd}
                      isDragOver={fltDragOverIdx === idx}
                      onDelete={handleFltDelete}
                    />
                  ))}
                </Box>
                <Button
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={handleFltAdd}
                  variant="outlined"
                  size="small"
                  sx={{ mt: 1.5 }}
                >
                  Add Filter
                </Button>
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snack.severity} variant="filled" onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
